/**
 * The account router (`backend/src/pivot/api/routes/account.py`): right to
 * erasure (constitution §V / PIVOT-063), right of access (PIVOT-115), and the
 * stored UI language.
 *
 * Erasure is irreversible. `DELETE /api/account` removes the user
 * row and everything cascading from it (profile, scores, consents,
 * notifications) plus the encrypted CV blob, in one operation. There is no
 * soft delete and no grace period — see PRIVACY.md, "Your right to erasure".
 * The caller is responsible for ending the session afterwards: the token still
 * parses, but the user it names no longer exists.
 */

import { httpClient } from './client'
import type { Locale } from '../types/domain'

/** Erases the signed-in user. Resolves on the backend's `204 No Content`. */
export function deleteAccount(signal?: AbortSignal): Promise<void> {
  return httpClient.delete('/account', signal)
}

/**
 * Everything Pivot holds about the signed-in user (`GET /api/account/export`,
 * GDPR Article 15). No consent gate on the backend — access is how a
 * candidate decides whether to keep consenting — so this has none either.
 *
 * The backend sends `Content-Disposition: attachment`, which only matters to
 * a browser navigating there directly; a fetch-based call like this one just
 * gets a JSON body back. `DownloadData` is what turns it into a saved file.
 * The shape is deliberately untyped (`Record<string, unknown>`): it is one
 * key per backend table, dumped generically so a column added later shows up
 * without a frontend change, and nothing here reads into it — it goes
 * straight to a file.
 */
export function exportAccountData(signal?: AbortSignal): Promise<Record<string, unknown>> {
  return httpClient.get('/account/export', signal)
}

/**
 * Persists an explicit change to the onboarding language
 * (`PUT /api/account/locale`).
 *
 * The header switch does not call this function. PIVOT-159 option 1 keeps
 * `users.locale` as the onboarding language that background jobs carry.
 */
export function updateLocale(locale: Locale, signal?: AbortSignal): Promise<void> {
  return httpClient.put('/account/locale', { locale }, signal)
}
